import { ChevronDown } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { cn } from '../lib/utils'
import { EmberLogo } from './EmberLogo'

export type PortalNavSubItem = { title: string; href: string }

export type PortalNavItem = {
  title: string
  href: string
  icon: ComponentType<{ className?: string }>
  subItems?: PortalNavSubItem[]
}

export type PortalLinkProps = {
  href: string
  className?: string | undefined
  title?: string | undefined
  onClick?: (() => void) | undefined
  children: ReactNode
}

export type PortalAccent = 'orange' | 'teal'

// Full class strings so Tailwind can detect them.
const ACCENTS: Record<PortalAccent, { active: string; icon: string; sub: string; badge: string }> =
  {
    orange: {
      active:
        'bg-orange-50 text-orange-700 border-orange-600 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-500',
      icon: 'text-orange-600 dark:text-orange-400',
      sub: 'text-orange-700 dark:text-orange-400',
      badge:
        'bg-orange-100 text-orange-800 ring-orange-600/10 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-900/50',
    },
    teal: {
      active:
        'bg-teal-50 text-teal-700 border-teal-600 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-500',
      icon: 'text-teal-600 dark:text-teal-400',
      sub: 'text-teal-700 dark:text-teal-400',
      badge:
        'bg-teal-100 text-teal-700 ring-teal-600/10 dark:bg-teal-900/30 dark:text-teal-400 dark:ring-teal-900/50',
    },
  }

const MOBILE_QUERY = '(max-width: 767px)'

/** Sidebar state: persisted desktop collapse + mobile drawer open/close. */
export function usePortalSidebar(storageKey: string, pathname: string) {
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  const setCollapsed = useCallback(
    (value: boolean) => {
      try {
        localStorage.setItem(storageKey, String(value))
      } catch {
        // storage unavailable — keep in-memory state only
      }
      setCollapsedState(value)
    },
    [storageKey],
  )

  // Close the drawer on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger
  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!mobileOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  // Close the drawer when the viewport grows to desktop size
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const handler = (e: MediaQueryListEvent) => {
      if (!e.matches) setMobileOpen(false)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Escape closes the drawer
  useEffect(() => {
    if (!mobileOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mobileOpen])

  /** Hamburger behaviour: drawer on mobile, collapse toggle on desktop. */
  const toggle = useCallback(() => {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      setMobileOpen((o) => !o)
    } else {
      setCollapsed(!collapsed)
    }
  }, [collapsed, setCollapsed])

  return { collapsed, setCollapsed, mobileOpen, setMobileOpen, toggle }
}

function isPathActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`)
}

function SidebarContent({
  groups,
  pathname,
  renderLink,
  collapsed,
  badge,
  accent,
  onNavigate,
}: {
  groups: PortalNavItem[][]
  pathname: string
  renderLink: (props: PortalLinkProps) => ReactNode
  collapsed: boolean
  badge: string
  accent: PortalAccent
  onNavigate?: () => void
}) {
  const colors = ACCENTS[accent]
  const [expandedHref, setExpandedHref] = useState<string | null>(null)

  const isItemActive = (item: PortalNavItem) =>
    item.subItems
      ? item.subItems.some((s) => isPathActive(pathname, s.href))
      : isPathActive(pathname, item.href)

  // Auto-expand the group containing the active route
  useEffect(() => {
    for (const group of groups) {
      for (const item of group) {
        if (item.subItems?.some((s) => isPathActive(pathname, s.href))) {
          setExpandedHref(item.href)
          return
        }
      }
    }
  }, [pathname, groups])

  return (
    <>
      <div
        className={cn(
          'h-14 flex items-center justify-center flex-shrink-0 border-b border-zinc-200 dark:border-zinc-800',
          collapsed ? 'px-2' : 'px-4',
        )}
      >
        {collapsed ? (
          <img
            src="/favicon_48x48.png"
            alt="Ember"
            className="w-8 h-8 flex-shrink-0"
            draggable={false}
          />
        ) : (
          <EmberLogo className="h-24" />
        )}
      </div>
      {!collapsed && (
        <div className="px-4 py-3">
          <span
            className={cn(
              'inline-flex w-full justify-center items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
              colors.badge,
            )}
          >
            {badge}
          </span>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto no-scrollbar py-2 px-2 space-y-1">
        {groups.map((group, groupIndex) => (
          <div key={group[0]?.href ?? groupIndex} className="space-y-1">
            {groupIndex > 0 && (
              <div className="border-t border-zinc-200 dark:border-zinc-800 mx-3 my-2" />
            )}
            {group.map((item) => {
              const active = isItemActive(item)
              const hasSub = !!item.subItems?.length
              const isOpen = expandedHref === item.href
              const rowClass = cn(
                'flex w-full items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors group border-l-2 text-left',
                active
                  ? cn(colors.active, 'font-semibold')
                  : 'border-transparent text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-50',
              )
              const iconClass = cn(
                'w-4 h-4 flex-shrink-0',
                active
                  ? colors.icon
                  : 'text-zinc-500 group-hover:text-zinc-900 dark:group-hover:text-zinc-50',
              )

              // Collapsed rail: icon only, link to the item (or its first sub-page)
              if (collapsed) {
                return (
                  <div key={item.href}>
                    {renderLink({
                      href: item.subItems?.[0]?.href ?? item.href,
                      title: item.title,
                      className: 'block',
                      onClick: onNavigate,
                      children: (
                        <div className={cn(rowClass, 'justify-center px-0')}>
                          <item.icon className={iconClass} />
                          <span className="sr-only">{item.title}</span>
                        </div>
                      ),
                    })}
                  </div>
                )
              }

              if (hasSub) {
                return (
                  <div key={item.href}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => setExpandedHref(isOpen ? null : item.href)}
                      className={rowClass}
                    >
                      <item.icon className={iconClass} />
                      <span className="flex-1 text-sm font-medium whitespace-nowrap">
                        {item.title}
                      </span>
                      <ChevronDown
                        className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                      />
                    </button>
                    <div
                      className={cn(
                        'grid transition-[grid-template-rows] duration-200 ease-out',
                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                      )}
                    >
                      <div className="overflow-hidden">
                        <div className="space-y-1 mt-1">
                          {item.subItems?.map((sub) => {
                            const subActive = isPathActive(pathname, sub.href)
                            return (
                              <div key={sub.href}>
                                {renderLink({
                                  href: sub.href,
                                  onClick: onNavigate,
                                  className: cn(
                                    'block pl-[38px] py-1.5 text-xs transition-colors',
                                    subActive
                                      ? cn(colors.sub, 'font-semibold')
                                      : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50',
                                  ),
                                  children: sub.title,
                                })}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <div key={item.href}>
                  {renderLink({
                    href: item.href,
                    className: 'block',
                    onClick: onNavigate,
                    children: (
                      <div className={rowClass}>
                        <item.icon className={iconClass} />
                        <span className="flex-1 text-sm font-medium whitespace-nowrap">
                          {item.title}
                        </span>
                      </div>
                    ),
                  })}
                </div>
              )
            })}
          </div>
        ))}
      </nav>
    </>
  )
}

export function PortalSidebar({
  groups,
  pathname,
  renderLink,
  badge,
  accent,
  collapsed,
  mobileOpen,
  onMobileOpenChange,
}: {
  groups: PortalNavItem[][]
  pathname: string
  renderLink: (props: PortalLinkProps) => ReactNode
  badge: string
  accent: PortalAccent
  collapsed: boolean
  mobileOpen: boolean
  onMobileOpenChange: (open: boolean) => void
}) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col h-screen sticky top-0 flex-shrink-0 border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 transition-all duration-200 ease-in-out',
          collapsed ? 'w-[64px]' : 'w-64',
        )}
      >
        <SidebarContent
          groups={groups}
          pathname={pathname}
          renderLink={renderLink}
          collapsed={collapsed}
          badge={badge}
          accent={accent}
        />
      </aside>

      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-0 z-40 md:hidden bg-black/50 transition-opacity duration-200',
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={() => onMobileOpenChange(false)}
        aria-hidden="true"
      />
      <aside
        aria-hidden={!mobileOpen}
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] flex flex-col md:hidden border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 transition-transform duration-200 ease-in-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <SidebarContent
          groups={groups}
          pathname={pathname}
          renderLink={renderLink}
          collapsed={false}
          badge={badge}
          accent={accent}
          onNavigate={() => onMobileOpenChange(false)}
        />
      </aside>
    </>
  )
}
